'use client';

import type { ComponentProps } from 'react';
import React, { memo, useContext, useMemo } from 'react';
import { mergeProps, RouterProvider as RARouterProvider } from 'react-aria';
import { useLinkProps } from '@react-aria/utils';
import type { LinkDOMProps } from '@react-types/shared';

export type LinkComponentType = React.ComponentType | 'a';

export type VaporRouterOptions = Partial<
  ComponentProps<typeof RARouterProvider>
> & {
  LinkComponent?: LinkComponentType;
};

const RouterContext = React.createContext<
  Pick<VaporRouterOptions, 'LinkComponent'>
>({
  LinkComponent: 'a',
});

/**
 * RouterProvider is a wrapper around react-aria's RouterProvider. It allows
 * Vapor components to use a custom Link component and navigate
 * function.
 *
 * @example
 * <RouterProvider
 *  LinkComponent={NextLink}
 *  navigate={navigate}
 *  useHref={useHref}
 * >
 *   {children}
 * </RouterProvider>
 * @returns
 */

function RouterProvider({
  LinkComponent,
  navigate,
  useHref,
  children,
}: Partial<VaporRouterOptions>) {
  const parentContext = useContext(RouterContext);

  const context = useMemo(
    () =>
      mergeProps({}, parentContext, {
        LinkComponent,
        navigate,
        useHref,
      }),
    [LinkComponent, navigate, useHref, parentContext]
  );

  return (
    <RouterContext.Provider value={context}>
      {/* @ts-expect-error FIXME(strictNullChecks) */}
      <RARouterProvider {...context}>{children}</RARouterProvider>
    </RouterContext.Provider>
  );
}

const _RouterProvider = memo(RouterProvider);
_RouterProvider.displayName = 'RouterProvider';
export { _RouterProvider as RouterProvider };

/**
 * useLink provides the necessary props to create a link component.
 * This is typically used alongside `useRouter` to trigger
 * client-side navigation.
 *
 * @example
 * const { LinkComponent, linkProps } = useLink({
 *  href: '/hello',
 * });
 *
 * const router = useRouter();
 *
 * const onClick = (e) => {
 *  e.preventDefault();
    router.open(e.currentTarget, e, props.href, undefined);
 * }
 *
 * <LinkComponent onClick={onClick} {...linkProps}>Hello</LinkComponent>
 */

export function useLink(props: LinkDOMProps) {
  const { LinkComponent } = useContext(RouterContext);
  const linkProps = useLinkProps(props);

  return {
    LinkComponent,
    linkProps: {
      ...linkProps,
      // @ts-expect-error FIXME(strictNullChecks)
      ...(props.routerOptions as object),
    },
  };
}
